import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { getRequest } from 'services';
import { SortDirection, SortColumn } from 'redux/annotations/annotations.type';
import { ReactComponent as Bell } from 'assets/svgs/icn-bell.svg';
import { Badge } from 'components/shared';

const BadgeWrapper = styled.div`
  cursor: pointer;
`;

type NeedReviewPollingProps = {
  setPagedQuery: (query: any) => void
};

const POLLING_INTERVAL_IN_SECONDS = 60;

const NeedReviewPolling = (props: NeedReviewPollingProps) => {
  const { setPagedQuery } = props;

  const [needReview, setNeedsReview] = useState<number>(0);
  const [notification, setNotification] = useState<{
    notification: Notification | null,
    needReview: number
  } | null>(null);
  const [pollDate, setPollDate] = useState<Date>(new Date());

  useEffect(() => {
    Notification.requestPermission();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPollDate(new Date());
      getRequest(`/photoSeries/needsReview?updatesFrom=${pollDate.toISOString()}`)
        .then((response) => {
          if (response.data > 0) {
            setNeedsReview(needReview + response.data);
          }
        });
    }, POLLING_INTERVAL_IN_SECONDS * 1000);
    return () => clearInterval(interval);
  }, [needReview, pollDate]);

  const handleNeedReviewRefresh = useCallback(() => {
    setPagedQuery({
      page: 1,
      pageSize: 5,
      sortDirection: SortDirection.Asc,
      sortColumn: SortColumn.CompletedAt
    });
    setNeedsReview(0);
    notification?.notification?.close();
    setNotification(null);
  }, [setPagedQuery, notification]);

  useEffect(() => {
    const createNotification = () : Notification | null => {
      if (Notification?.permission !== 'granted') return null;
      const browserNotification = new Notification('New photo series need review!', {
        requireInteraction: true,
        body: 'Click to refresh need review tab'
      });

      browserNotification.onclick = (event: any) => {
        event.preventDefault();
        window.focus();
        handleNeedReviewRefresh();
        browserNotification.close();
      };

      browserNotification.onclose = () => {
        setNotification({
          notification: null,
          needReview
        });
      };

      return browserNotification;
    };

    if (needReview === 0) return;
    if (!notification?.notification && notification?.needReview !== needReview) {
      setNotification({
        notification: createNotification(),
        needReview
      });
    }
  }, [needReview, notification, setNotification, handleNeedReviewRefresh]);

  if (needReview === 0) {
    return <Badge variant="gray" txt="0 new entries" icon={<Bell />} />;
  }

  return (
    <OverlayTrigger
      key="right"
      placement="right"
      overlay={
        <Tooltip>
          New photo series need review! Click on badge to refresh page
        </Tooltip>
      }
    >
      <BadgeWrapper onClick={handleNeedReviewRefresh}>
        <Badge variant="green" txt={`${needReview} new entries`} icon={<Bell />} pulse />
      </BadgeWrapper>
    </OverlayTrigger>
  );
};

export { NeedReviewPolling };
